import React, { useState, useEffect, useCallback } from "react";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

import {
  Typography,
  Button,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  Slide,
  DialogActions,
  IconButton,
  Divider,
  MenuItem,
  InputAdornment,
  Box,
  Switch,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDropzone } from "react-dropzone";
import useStyles from "./style";
import { useFormik } from "formik";
import * as yup from "yup";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { setNewtimezone, setSystemlatitude, setSystemlongitude } from "Redux/TimeZone/actions";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";

// Api routes
import settingsApi from "Services/Settings";
import servicetypeapi from "Services/Service";

// Data
import countryCodeData from "./data/countryCode/index.json";
import timeZoneData from "./data/timeZone.json";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Custom Vibrant Green Switch matching the reference design
const GreenSwitch = styled(Switch)(({ theme }) => ({
  width: 44,
  height: 24,
  padding: 0,
  display: "flex",
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(20px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#4ADE80",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgba(0,0,0,0.15)",
    width: 20,
    height: 20,
    borderRadius: 10,
  },
  "& .MuiSwitch-track": {
    borderRadius: 12,
    opacity: 1,
    backgroundColor: "#CBD5E1",
    boxSizing: "border-box",
  },
}));

const Settings = () => {
  // BusinessSettings upload (default driver document file -> defaultDriverDocumentPaths)
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadedPathUrl, setUploadedPathUrl] = useState("");

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
  };

  const uploadDefaultDriverDocument = async (acceptedFiles) => {
    dispatch(showLoader("Loading please wait..."));

    const onFailure = (e) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: e?.response?.data?.message || "Failed to upload the file",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    };

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setUploadDialogOpen(false);
        getMasterValues();
      }
    };

    try {
      if (!acceptedFiles || acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];
      const formData = new FormData();
      formData.append("file", file);

      formData.append("timeZone", formik?.values?.timeZone || values?.timeZone || "");
      formData.append(
        "defaultDriverDocumentPaths",
        formik?.values?.defaultDriverDocumentPaths || uploadedPathUrl || ""
      );

      const demoaccount_id = formik?.values?.demoaccount_id || "";
      const userid = formik?.values?.userid || "";
      if (demoaccount_id) formData.append("demoaccount_id", demoaccount_id);
      if (!demoaccount_id && userid) formData.append("userid", userid);

      settingsApi
        .updateBusinessSettings(formData)
        .then(onSuccess)
        .catch(onFailure);
    } catch (e) {
      onFailure(e);
    }
  };

  const demoaccount_id = useSelector(
    (state) => state?.AuthReducer?.demoaccount_id || ""
  );

  const classes = useStyles();
  const dispatch = useDispatch();

  // States
  const [values, setValues] = useState([]);
  const [serviceTypeOptions, setServiceTypeOptions] = useState([]);

  const handleSettings = (formValues) => {
    dispatch(showLoader("Loading please wait..."));

    const timeZ = timeZoneData.find(
      (zone) => zone.utc[0] === formValues.timeZone
    );

    let timeZoneOffset;
    if (timeZ) {
      timeZoneOffset = timeZ.offset;
    } else {
      console.error(`Time zone with value '${formValues.timeZone}' not found`);
    }

    function offsetToMinutes(offset) {
      return offset * 60;
    }

    const data = {
      isMailEnable: formValues?.isMailEnable === "ENABLE" ? 1 : 0,
      isSmsEnable: formValues?.isSmsEnable === "ENABLE" ? 1 : 0,
      countryCode: formValues?.countryCode,
      driverAcceptenceTimeOut: formValues?.driverAcceptTimeout,
      driverSearchRadius: formValues?.driverSearchRadius,
      distanceUnit: formValues?.distanceUnit,
      autoAssign: formValues?.autoAssign === "ENABLE" ? 1 : 0,
      siteCountry: formValues?.country,
      address: formValues?.address,
      mapZoomLevel: formValues?.mapZoomLevel,
      timeZone: formValues?.timeZone,
      timeZoneOffset: offsetToMinutes(timeZoneOffset),
      availableTips: formValues?.availableTips,
      enableDriverAutoApprovalDefaults:
        formValues?.enableDriverAutoApprovalDefaults === "ENABLE" ? 1 : 0,
      defaultDriverServiceTypeId: formValues?.defaultDriverServiceTypeId || null,
      driver_increment: formValues?.driver_increment,
      findDriverBeforeScheduleMinutes: formValues?.findDriverBeforeScheduleMinutes,
    };

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.message || "Updated settings successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        formik?.resetForm();
        setValues(null);
        getMasterValues();
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Error while update settings",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    settingsApi.updateBusinessSettings({ ...data }).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: {
      isMailEnable: "ENABLE",
      isSmsEnable: "ENABLE",
      countryCode: "+91",
      driverAcceptTimeout: "60",
      driverSearchRadius: "100",
      distanceUnit: "KM",
      autoAssign: "ENABLE",
      country: "India",
      address: "coimbatore",
      availableTips: ["5", "10", "15", "20"],
      mapZoomLevel: "10",
      timeZone: "America/Anchorage",
      enableDriverAutoApprovalDefaults: "ENABLE",
      defaultDriverServiceTypeId: "",
      driver_increment: "1.0",
      findDriverBeforeScheduleMinutes: "5",
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      availableTips: yup
        .array()
        .of(yup.string().nullable().label("Tip Level").default("")),
      timeZone: yup.string().required("This field is required"),
      isMailEnable: yup.string().required("This field is required"),
      isSmsEnable: yup.string().required("This field is required"),
      driverAcceptTimeout: yup
        .number()
        .typeError("Must be a number")
        .min(0, "Must be greater than or equal to 0")
        .max(1000, "Must be less than or equal to 1000"),
      driverSearchRadius: yup
        .number()
        .typeError("Must be a number")
        .min(0, "Must be greater than or equal to 0")
        .max(1000, "Must be less than or equal to 1000"),
      distanceUnit: yup.string().required("This field is required"),
      autoAssign: yup.string().required("This field is required"),
      driver_increment: yup
        .number()
        .typeError("Must be a number")
        .min(0, "Must be greater than or equal to 0"),
      findDriverBeforeScheduleMinutes: yup
        .number()
        .typeError("Must be a number")
        .min(0, "Must be greater than or equal to 0"),
      mapZoomLevel: yup
        .number()
        .typeError("Must be a number")
        .min(0, "Must be greater than or equal to 0")
        .max(1000, "Must be less than or equal to 1000"),
    }),
    onSubmit: handleSettings,
  });

  const onUploadPickClick = () => {
    setUploadDialogOpen(true);
  };

  const UploadDefaultDriverDocumentDialog = () => {
    const { getRootProps, getInputProps, open } = useDropzone({
      noClick: true,
      noKeyboard: true,
      maxFiles: 1,
      accept: {
        "image/*": [".jpeg", ".jpg", ".png"],
        "application/pdf": [".pdf"],
      },
      onDrop: (acceptedFiles) => uploadDefaultDriverDocument(acceptedFiles),
    });

    return (
      <Dialog
        open={uploadDialogOpen}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleCloseUploadDialog}
        maxWidth={"md"}
        aria-describedby="dialog-box"
        className={classes.root}
      >
        <DialogTitle>
          <section className={classes.dialogTitleContainer}>
            <Typography variant="body1" style={{ fontWeight: 600 }}>
              Upload Default Driver Document
            </Typography>
            <IconButton aria-label="close" onClick={handleCloseUploadDialog}>
              <CloseIcon />
            </IconButton>
          </section>
        </DialogTitle>
        <DialogContent>
          <div className={classes.uploadAreaWrapper}>
            <section
              {...getRootProps()}
              onClick={open}
              className={classes.fileUploadArea}
            >
              <input {...getInputProps()} />
              <div className={classes.fileUploadContentsStyle}>
                <div>
                  <div className={classes.fileIconWrapper}>
                    <InsertDriveFileOutlinedIcon style={{ fontSize: "80px" }} />
                    <div className={classes.uploadIconWrapper}>
                      <FileUploadOutlinedIcon fontSize="small" />
                    </div>
                  </div>
                </div>
                <Typography variant="body1" style={{ marginTop: "10px" }}>
                  Drag and Drop Files Here or{" "}
                  <span style={{ textDecoration: "underline", color: "#0F172A", fontWeight: 600 }}>Choose File</span>
                </Typography>
              </div>
            </section>
            {uploadedPathUrl && (
              <div className={classes.previewDocumentWrapper}>
                <Typography variant="body2" style={{ fontWeight: 600 }}>Selected/Uploaded Document</Typography>
                <Typography variant="caption" style={{ wordBreak: "break-word" }}>
                  {uploadedPathUrl}
                </Typography>
              </div>
            )}
          </div>
        </DialogContent>
        <Divider />
        <DialogActions>
          <section className={classes.dialogActions}>
            <section className={classes.closeBtn}>
              <Button onClick={handleCloseUploadDialog}>
                Close
              </Button>
            </section>
            <section className={classes.closeBtn}>
              <Button variant="contained" onClick={() => open()} style={{ backgroundColor: "#0F172A", color: "#fff" }}>Upload</Button>
            </section>
          </section>
        </DialogActions>
      </Dialog>
    );
  };

  // Get Business Settings
  const getMasterValues = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(setNewtimezone(res?.data?.data?.timeZone));
        dispatch(setSystemlatitude(res?.data?.data?.latitude));
        dispatch(setSystemlongitude(res?.data?.data?.longitude));
        setValues(res?.data?.data);
        setUploadedPathUrl(
          res?.data?.data?.defaultDriverDocumentPaths?.[0] || ""
        );
      } else {
        setValues([]);
        setUploadedPathUrl("");
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    settingsApi.getBusinessSettings().then(onSuccess, onFailure);
  }, []);

  useEffect(() => {
    getMasterValues();
  }, [getMasterValues]);

  // Fetch service types for Default Driver Service Type dropdown
  useEffect(() => {
    const fetchServiceTypes = async () => {
      try {
        const res = await servicetypeapi.getServiceswithfaremodel();
        const list = res?.data?.data.data || res?.data?.data?.data || [];
        setServiceTypeOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        setServiceTypeOptions([]);
      }
    };

    fetchServiceTypes();
  }, []);

  // Formik - Set fetched values for master
  useEffect(() => {
    if (!_.isEmpty(values)) {
      if (values?.isMailEnable === 1) {
        formik.setFieldValue("isMailEnable", "ENABLE");
      } else {
        formik.setFieldValue("isMailEnable", "DISABLE");
      }
      if (values?.isSmsEnable === 1) {
        formik.setFieldValue("isSmsEnable", "ENABLE");
      } else {
        formik.setFieldValue("isSmsEnable", "DISABLE");
      }

      formik.setFieldValue("countryCode", values?.countryCode || "+91");
      formik.setFieldValue(
        "driverAcceptTimeout",
        values?.driverAcceptenceTimeOut !== undefined && values?.driverAcceptenceTimeOut !== null ? values?.driverAcceptenceTimeOut : "60"
      );
      formik.setFieldValue("driverSearchRadius", values?.driverSearchRadius !== undefined && values?.driverSearchRadius !== null ? values?.driverSearchRadius : "100");
      formik.setFieldValue("distanceUnit", values?.distanceUnit || "KM");

      if (values?.autoAssign === 1) {
        formik.setFieldValue("autoAssign", "ENABLE");
      } else {
        formik.setFieldValue("autoAssign", "DISABLE");
      }

      formik.setFieldValue("country", values?.siteCountry || "");
      formik.setFieldValue("state", values?.state || "");
      formik.setFieldValue("city", values?.city || "");
      formik.setFieldValue("address", values?.address || "");
      formik.setFieldValue("mapZoomLevel", values?.mapZoomLevel !== undefined && values?.mapZoomLevel !== null ? values?.mapZoomLevel : "10");
      formik.setFieldValue("timeZone", values?.timeZone || "America/Anchorage");

      if (Array.isArray(values?.availableTips) && values.availableTips.length === 4) {
        formik.setFieldValue("availableTips", values.availableTips);
      }

      formik.setFieldValue("driver_increment", values?.driver_increment || "1.0");
      formik.setFieldValue("findDriverBeforeScheduleMinutes", values?.findDriverBeforeScheduleMinutes || "5");

      formik.setFieldValue(
        "enableDriverAutoApprovalDefaults",
        values?.enableDriverAutoApprovalDefaults === 1 ? "ENABLE" : "DISABLE"
      );

      formik.setFieldValue(
        "defaultDriverServiceTypeId",
        values?.defaultDriverServiceTypeId?._id || values?.defaultDriverServiceTypeId || ""
      );
    }
  }, [values]);

  return (
    <Box className={classes.cardContainer}>
      <UploadDefaultDriverDocumentDialog />

      {/* Header Bar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography className={classes.headerTitle}>Business Settings</Typography>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography className={classes.breadcrumbText}>Admin</Typography>
          <Typography className={classes.breadcrumbText}>/</Typography>
          <Typography className={classes.breadcrumbActive}>Business Settings</Typography>
        </Box>
      </Box>

      {/* Cards Grid Container */}
      <Grid container spacing={3}>
        {/* Row 1, Left: Driver & Vehicle Defaults */}
        <Grid item xs={12} md={7}>
          <div className={classes.sectionCard}>
            <div className={classes.cardHeader}>
              <DirectionsCarOutlinedIcon className={classes.cardIcon} />
              <Typography className={classes.cardTitle}>Driver & Vehicle Defaults</Typography>
            </div>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <Typography className={classes.label}>Default Driver Document</Typography>
                <div className={classes.dropzoneBox}>
                  <div className={classes.docIconCircle}>
                    <DescriptionOutlinedIcon fontSize="small" />
                  </div>
                  <Button
                    variant="contained"
                    className={classes.uploadBtn}
                    onClick={onUploadPickClick}
                    disabled={Boolean(demoaccount_id)}
                    startIcon={<FileUploadOutlinedIcon style={{ fontSize: 14 }} />}
                  >
                    UPLOAD/CHANGE
                  </Button>
                </div>
                {uploadedPathUrl && (
                  <Typography
                    variant="caption"
                    style={{ wordBreak: "break-word", marginTop: 6, display: "block", color: "#64748B" }}
                  >
                    {uploadedPathUrl}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={6} display="flex" flexDirection="column" justifyContent="space-between">
                <div>
                  <Typography className={classes.label}>Default Driver Service Type</Typography>
                  <TextField
                    name="defaultDriverServiceTypeId"
                    id="defaultDriverServiceTypeId"
                    select
                    value={formik?.values?.defaultDriverServiceTypeId || ""}
                    onChange={formik?.handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    className={classes.inputField}
                    disabled={Boolean(demoaccount_id)}
                  >
                    <MenuItem value={""}>None</MenuItem>
                    {serviceTypeOptions.map((services) => (
                      <MenuItem key={services._id} value={services._id}>
                        {services.serviceName}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>

                <div className={classes.toggleBox} style={{ marginTop: 16 }}>
                  <Typography className={classes.toggleBoxTitle}>Auto-Approval Defaults</Typography>
                  <GreenSwitch
                    checked={formik?.values?.enableDriverAutoApprovalDefaults === "ENABLE"}
                    onChange={(e) =>
                      formik?.setFieldValue(
                        "enableDriverAutoApprovalDefaults",
                        e.target.checked ? "ENABLE" : "DISABLE"
                      )
                    }
                    disabled={Boolean(demoaccount_id)}
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        </Grid>

        {/* Row 1, Right: Tipping Policy */}
        <Grid item xs={12} md={5}>
          <div className={classes.sectionCard}>
            <div className={classes.cardHeader}>
              <PaymentsOutlinedIcon className={classes.cardIcon} />
              <Typography className={classes.cardTitle}>Tipping Policy</Typography>
            </div>
            <Grid container spacing={2}>
              {["1", "2", "3", "4"].map((tipLevel, index) => (
                <Grid item xs={6} key={index}>
                  <Typography className={classes.label} style={{ fontSize: "11px", letterSpacing: "0.5px" }}>
                    TIP OPTION {tipLevel}
                  </Typography>
                  <TextField
                    name={`availableTips[${index}]`}
                    id={`availableTips-${index}`}
                    value={formik?.values?.availableTips?.[index] ?? ""}
                    onChange={formik?.handleChange}
                    type="text"
                    fullWidth
                    variant="outlined"
                    size="small"
                    className={classes.inputField}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography style={{ fontWeight: 700, color: "#0F172A", fontSize: "14px" }}>
                            $
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    disabled={Boolean(demoaccount_id)}
                  />
                </Grid>
              ))}
            </Grid>
          </div>
        </Grid>

        {/* Row 2, Left: Communication */}
        <Grid item xs={12} md={4}>
          <div className={classes.sectionCard}>
            <div className={classes.cardHeader}>
              <NotificationsNoneOutlinedIcon className={classes.cardIcon} />
              <Typography className={classes.cardTitle}>Communication</Typography>
            </div>

            <div className={classes.toggleBox} style={{ marginBottom: 12 }}>
              <div>
                <Typography className={classes.toggleBoxTitle}>Email Notifications</Typography>
                <Typography className={classes.toggleBoxSubtitle}>Send transactional emails</Typography>
              </div>
              <GreenSwitch
                checked={formik?.values?.isMailEnable === "ENABLE"}
                onChange={(e) =>
                  formik?.setFieldValue("isMailEnable", e.target.checked ? "ENABLE" : "DISABLE")
                }
                disabled={Boolean(demoaccount_id)}
              />
            </div>

            <div className={classes.toggleBox} style={{ marginBottom: 16 }}>
              <div>
                <Typography className={classes.toggleBoxTitle}>SMS Alerts</Typography>
                <Typography className={classes.toggleBoxSubtitle}>Real-time ride updates</Typography>
              </div>
              <GreenSwitch
                checked={formik?.values?.isSmsEnable === "ENABLE"}
                onChange={(e) =>
                  formik?.setFieldValue("isSmsEnable", e.target.checked ? "ENABLE" : "DISABLE")
                }
                disabled={Boolean(demoaccount_id)}
              />
            </div>

            <div>
              <Typography className={classes.label}>Default Country Code</Typography>
              <TextField
                name="countryCode"
                id="countryCode"
                value={formik?.values?.countryCode || ""}
                onChange={formik?.handleChange}
                type="text"
                fullWidth
                variant="outlined"
                size="small"
                className={classes.inputField}
                disabled={Boolean(demoaccount_id)}
              />
            </div>
          </div>
        </Grid>

        {/* Row 2, Right: Operational Rules */}
        <Grid item xs={12} md={8}>
          <div className={classes.sectionCard}>
            <div className={classes.cardHeader}>
              <SettingsOutlinedIcon className={classes.cardIcon} />
              <Typography className={classes.cardTitle}>Operational Rules</Typography>
            </div>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography className={classes.label}>Accept Timeout (sec)</Typography>
                <TextField
                  name="driverAcceptTimeout"
                  id="driverAcceptTimeout"
                  value={formik?.values?.driverAcceptTimeout || ""}
                  onChange={formik?.handleChange}
                  type="text"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.inputField}
                  disabled={Boolean(demoaccount_id)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography className={classes.label}>Distance Unit</Typography>
                <TextField
                  name="distanceUnit"
                  id="distanceUnit"
                  select
                  value={formik?.values?.distanceUnit || "KM"}
                  onChange={formik?.handleChange}
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.inputField}
                  disabled={Boolean(demoaccount_id)}
                >
                  <MenuItem value="KM">km</MenuItem>
                  <MenuItem value="MILES">Miles</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography className={classes.label}>Map Zoom Level</Typography>
                <TextField
                  name="mapZoomLevel"
                  id="mapZoomLevel"
                  value={formik?.values?.mapZoomLevel || ""}
                  onChange={formik?.handleChange}
                  type="text"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.inputField}
                  disabled={Boolean(demoaccount_id)}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography className={classes.label}>Search Radius (km)</Typography>
                <TextField
                  name="driverSearchRadius"
                  id="driverSearchRadius"
                  value={formik?.values?.driverSearchRadius || ""}
                  onChange={formik?.handleChange}
                  type="text"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.inputField}
                  disabled={Boolean(demoaccount_id)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography className={classes.label}>Driver Increment</Typography>
                <TextField
                  name="driver_increment"
                  id="driver_increment"
                  value={formik?.values?.driver_increment || ""}
                  onChange={formik?.handleChange}
                  type="number"
                  inputProps={{ step: "0.01" }}
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.inputField}
                  disabled={Boolean(demoaccount_id)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                {/* Empty column for grid alignment */}
              </Grid>

              <Grid item xs={12} sm={4}>
                <div className={classes.toggleBox}>
                  <Typography className={classes.toggleBoxTitle}>Auto Assign</Typography>
                  <GreenSwitch
                    checked={formik?.values?.autoAssign === "ENABLE"}
                    onChange={(e) =>
                      formik?.setFieldValue("autoAssign", e.target.checked ? "ENABLE" : "DISABLE")
                    }
                    disabled={Boolean(demoaccount_id)}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography className={classes.label}>Schedule Lead Time (min)</Typography>
                <TextField
                  name="findDriverBeforeScheduleMinutes"
                  id="findDriverBeforeScheduleMinutes"
                  value={formik?.values?.findDriverBeforeScheduleMinutes || ""}
                  onChange={formik?.handleChange}
                  type="number"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.inputField}
                  disabled={Boolean(demoaccount_id)}
                />
              </Grid>
            </Grid>
          </div>
        </Grid>

        {/* Row 3, Full Width: Localization & Region */}
        <Grid item xs={12}>
          <div className={classes.sectionCard}>
            <div className={classes.cardHeader}>
              <LanguageOutlinedIcon className={classes.cardIcon} />
              <Typography className={classes.cardTitle}>Localization & Region</Typography>
            </div>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography className={classes.label}>Primary Country</Typography>
                <TextField
                  inputProps={{ form: { autocomplete: 'off' } }}
                  name="country"
                  id="country"
                  select
                  value={formik?.values?.country || ""}
                  onChange={formik?.handleChange}
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.inputField}
                  disabled={Boolean(demoaccount_id)}
                >
                  {countryCodeData.map((country) => (
                    <MenuItem key={country.code} value={country.name}>
                      {`${country.code} (${country.name})`}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography className={classes.label}>Headquarters Address</Typography>
                <TextField
                  name="address"
                  id="address"
                  value={formik?.values?.address || ""}
                  onChange={formik?.handleChange}
                  type="text"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.inputField}
                  disabled={Boolean(demoaccount_id)}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography className={classes.label}>Server Time Zone</Typography>
                <TextField
                  name="timeZone"
                  id="timeZone"
                  select
                  value={formik?.values?.timeZone || ""}
                  onChange={formik?.handleChange}
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.inputField}
                  disabled={Boolean(demoaccount_id)}
                >
                  {Object.keys(timeZoneData).map((timeZoneValue) => (
                    <MenuItem
                      key={timeZoneValue}
                      value={timeZoneData[timeZoneValue].utc[0]}
                    >
                      {timeZoneData[timeZoneValue].text} ({timeZoneData[timeZoneValue].offset})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </div>
        </Grid>
      </Grid>

      {/* Action Button */}
      <Box display="flex" justifyContent="flex-end" mt={3}>
        <Button
          variant="contained"
          className={classes.updateBtn}
          onClick={formik.handleSubmit}
          disabled={Boolean(demoaccount_id)}
        >
          Update
        </Button>
      </Box>
    </Box>
  );
};

export default Settings;
